import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { FILE_SCOPE_REQUEST } from '../../../../constants/file'
import { SIZE } from '../../../../constants/size'
import { getItemsIds } from '../../../../utils/common'
import { stopPropagation } from '../../../../utils/events'
import { Field } from '../../../Field'
import { FileList, FilePreview, useFileUpload } from '../../../File'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../../FilesPreviewGallery'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../Form'
import {
  getInitialGoogleMapModalValues,
  GoogleMapApiProvider,
  GoogleMapModal,
} from '../../../Google'
import { Content } from '../../../Modal'
import {
  BUILDING_FORM_ADDRESS_FIELD_MAX_LENGTH,
  BUILDING_FORM_FIELD_NAME_ADDRESS,
  BUILDING_FORM_FIELD_NAME_AVATAR,
  BUILDING_FORM_FIELD_NAME_INSURANCES,
  BUILDING_FORM_INSURANCE_INITIAL_VALUES,
} from '../BuildingForm.constants'
import { BuildingFormAdditionalFields } from '../BuildingFormAdditionalFields'
import { BuildingFormInsuranceFields } from '../BuildingFormInsuranceFields'

import { AddressField } from './AddressField'
import { useResetUpdateManagerCheckbox } from './BuildingFormFields.hooks'
import { BuildingGroupField } from './BuildingGroupField'
import { BuildingNumberField } from './BuildingNumberField'
import { BuildingTypeField } from './BuildingTypeField'
import { ContractStartDateField } from './ContractStartDateField'
import { ManagerField } from './ManagerField'
import { NotesField } from './NotesField'

import * as styles from './BuildingFormFields.module.css'

function BuildingFormFields({
  ContentComponent = Content,
  contentComponentProps: {
    className: contentClassName = styles.content,
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  files,
  notifications,
  setFiles,
  submitButtonText,
  onCancel,
  onRemoveNotification,
  onSetNotification,
}) {
  const { t } = useTranslation()

  const { dirty, isSubmitting, setFieldValue, values } = useFormikContext()

  const [showAddressModal, setShowAddressModal] = useState(false)

  const {
    filesError,
    filesUploading,
    handleExcludeFile,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onSetFiles: setFiles,
  })

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({ files })

  // Side effect to reset update manager checkbox
  // If responsible managers length === 0
  useResetUpdateManagerCheckbox()

  useFormValidationErrorsNotification({
    files: { filesError, filesUploading },
    onRemoveNotification,
    onSetNotification,
  })

  function handleDeleteFile(id) {
    if (getItemsIds(files)?.includes(id)) {
      handleExcludeFile(id)
    } else {
      handleRemoveFile(id)
    }
  }

  function handleCancel() {
    onCancel({
      dirty,
      files,
    })
  }

  function handleShowAddressModal(event) {
    stopPropagation(event)

    setShowAddressModal(true)
  }

  function handleCloseAddressModal() {
    setShowAddressModal(false)
  }

  function handleSubmitAddressForm(value) {
    setFieldValue(BUILDING_FORM_FIELD_NAME_ADDRESS, value)
    setShowAddressModal(false)
  }

  function handleAddInsurance() {
    const nextInsuranceIndex =
      values[BUILDING_FORM_FIELD_NAME_INSURANCES].length

    setFieldValue(
      `${BUILDING_FORM_FIELD_NAME_INSURANCES}.${nextInsuranceIndex}`,
      BUILDING_FORM_INSURANCE_INITIAL_VALUES
    )
  }

  function handleChangeAvatar(nextAvatar) {
    setFieldValue(BUILDING_FORM_FIELD_NAME_AVATAR, nextAvatar)
  }

  function renderFile(file, key) {
    return (
      <FilePreview
        allowAvatarSelection
        disabled={isSubmitting}
        file={file}
        isSelected={file.id === values[BUILDING_FORM_FIELD_NAME_AVATAR]}
        key={key}
        setAvatar={handleChangeAvatar}
        onClick={handleClickFile}
        onRemoveFile={handleDeleteFile}
      />
    )
  }

  const showFiles =
    files?.length > 0 || filesUploading?.length > 0 || filesError?.length > 0

  return (
    <GoogleMapApiProvider>
      <FormBody
        actionsProps={{
          ButtonAdd:
            values[BUILDING_FORM_FIELD_NAME_INSURANCES].length === 0
              ? undefined
              : false,
          ButtonClear: false,
          addProps: {
            disabled: isSubmitting,
            title: 'Modal.AddInsuranceBtnKey',
            onClick: handleAddInsurance,
          },
          cancelProps: {
            onClick: handleCancel,
          },
          className: styles.actions,
          isSubmitting,
          submitProps: {
            isLoading: isSubmitting,
            title: submitButtonText,
          },
          uploadProps: {
            onFinishUpload: handleUploadFile,
            onStartUpload: handleStartUploading,
            onUploadErrors: handleUploadErrors,
          },
        }}
      >
        <ContentComponent
          {...contentComponentProps}
          className={contentClassName}
          size={contentSize}
        >
          <FormInlineNotifications notifications={notifications} />

          <FormFieldsContainer className={styles.formFields}>
            <AddressField onShowMap={handleShowAddressModal} />
            <BuildingGroupField />
            <BuildingNumberField />
            <BuildingTypeField />
            <ContractStartDateField />
            <ManagerField />
            <NotesField />

            <BuildingFormAdditionalFields />

            <BuildingFormInsuranceFields />

            {showFiles ? (
              <Field
                shouldAdaptToMobileScreen
                disabled={isSubmitting}
                label={`${t('Building.AttachLabelKey')}:`}
              >
                <FileList
                  errorItems={filesError}
                  files={files}
                  renderItem={renderFile}
                  uploadingItems={filesUploading}
                  onRemoveFile={handleRemoveFile}
                />
              </Field>
            ) : null}
            <FilesPreviewGallery
              files={files}
              initActive={activeFileIndex}
              scope={FILE_SCOPE_REQUEST}
              onClose={handleCloseFilePreview}
            />
          </FormFieldsContainer>
        </ContentComponent>
      </FormBody>
      {showAddressModal ? (
        <GoogleMapModal
          editMode
          addressSelectProps={{
            maxLength: BUILDING_FORM_ADDRESS_FIELD_MAX_LENGTH,
          }}
          buttonText='Modal.BuildingSaveAddressBtnKey'
          initialValues={getInitialGoogleMapModalValues(
            values[BUILDING_FORM_FIELD_NAME_ADDRESS]
          )}
          title='Modal.AddAddressTitleKey'
          onClose={handleCloseAddressModal}
          onSubmit={handleSubmitAddressForm}
        />
      ) : null}
    </GoogleMapApiProvider>
  )
}

export default BuildingFormFields
